import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { EngineServiceType, ServiceType } from '../_models/ServiceType';
import { Engine } from '../_models/Engine';
import { BaseService } from '../_shared/base.service';
import { getUrl } from '../_shared/utils';
import { JWTTokenService } from './jwtToken.service';
import { CueConfig } from '../_models/CueConfig';

export class EngineStats {
    uniqueDevices: number;
    triggeredDevices: number;
    misfiredDevices: number;
    totalTriggers: number;
    triggersHeard: number;
    successfulTriggers: number;
    misfires: number;
    deviceTriggerSuccessRate: number;
    totalEngineAuths: number;
    uniqueEngineAuths: number;
}

@Injectable()
export class EngineService extends BaseService implements OnDestroy {
    userToken: any;

    private engineServiceTypes: EngineServiceType[] = [];

    private engineServiceTypeList = [
        'lightshow', 
        'selfiecam', 
        'triviagame', 
        'genericengine', 
        'upn', 
        'networksync',
        'coupons', 
        'venueedit',
        'fundraising'
      ];

    constructor(
        private http: HttpClient,
        jwtTokenService: JWTTokenService) {
        super(http, jwtTokenService);
    }

    ngOnDestroy() {
        this.clearServiceTypeCache();
    }

    public clearServiceTypeCache() {
        this.engineServiceTypes = [];
    }

    public getEngines(): Observable<any> {
        return this.http.get<any>(getUrl('engine'), this.requestOptionsAuthenticated());
    }

    public filterEngineStats(filterData: any): Observable<EngineStats> {

        return this.http.post<EngineStats>(getUrl(`engine/stats/`), filterData, this.requestOptionsAuthenticated());
    }

    public generateEngineReport(engineId: number, startDate: string, endDate: string): Observable<Blob> {
        const dates = { engineId: engineId, startDate: startDate, endDate: endDate };

        const options = this.requestOptionsAuthenticated();
        options['responseType'] = 'blob';

        return this.http.post<Blob>(getUrl(`engine/report/`), dates, options);
    }

    public getEngineByAppStoreName(appStoreName: string): Observable<Engine> {
        return this.http.get<Engine>(getUrl(`engine/app/${appStoreName}`), this.requestOptionsAuthenticated());
    }

    public toggleEngineActive(engineId: number): Observable<any[]> {
        return this.http.get<any[]>(getUrl(`engine/toggle-active/${engineId}`), this.requestOptionsAuthenticated());
    }

    public createEngine(engineData: any): Observable<boolean> {
        return this.http.post<any>(getUrl(`engine/create`), engineData, this.requestOptionsAuthenticated());
    }

    public getEngineServiceTypes(): Observable<ServiceType[]> {
        return this.http.get<ServiceType[]>(getUrl('engine/service-type'), this.requestOptionsAuthenticated());
    }

    public hasServiceType(typeName: string): Observable<boolean> {
        if(this.engineServiceTypes[typeName.toLowerCase()] != undefined) {
            return of(this.engineServiceTypes[typeName.toLowerCase()]);
        } else {
            return this.HttpPost<EngineServiceType[]>('engine/has-service-type/', this.engineServiceTypeList)
            .pipe(map(response => {
                this.engineServiceTypes = response;
                return this.engineServiceTypes[typeName.toLowerCase()] ?? false; 
            }));
        }
    }

    public getServiceCount() {
        return this.engineServiceTypes ? Object.keys(this.engineServiceTypes).filter(k => this.engineServiceTypes[k] === true).length : 0;
      }

    public getAllowedEngineServiceTypes() : Observable<EngineServiceType[]> {

        if(!this.engineServiceTypes || this.engineServiceTypes.length < 1) {

            return this.HttpPost<EngineServiceType[]>('engine/has-service-type/', this.engineServiceTypeList)
                .pipe(map(response => {
                    this.engineServiceTypes = response;
                    return response;
                }));

        } else {

            return of(this.engineServiceTypes);
        }
    }

    public getConfigs(): Observable<any> {
        return this.CachedHttpGet<boolean>('engine/config');
    }

    public getConfigById(id: number) : Observable<CueConfig> {
        return this.HttpGet<CueConfig>(`engine/config/${id}`);
    }

    public createConfig(config):Observable<any> {
        return this.HttpPost<any>('engine/config/create', config);
    }

    public updateConfig(config: CueConfig) : Observable<any> {
        return this.HttpPost<any>('engine/config/update', config);
    }

    public checkEngineServices(): Observable<any> {
        return this.HttpGet<any>('engine/test');
    }

    private requestOptionsAuthenticated() {
        const requestOptions = {
            headers: new HttpHeaders()
                .set('Content-type', 'application/json')
                .append('Authorization', 'Bearer ' + this.jwtTokenService.getToken('portal')),
        };
        return requestOptions;
    }

}

